import { createContext, useContext, useState, type ReactNode } from "react";

// Global "dev mode" flag, toggled from the secret Dev options panel. When on,
// dev-only affordances light up across the app (sample-test seeding, onboarding
// toggles, the failed-generation sample loader). Persisted to localStorage so
// it survives navigation/refresh.
const DEV_MODE_KEY = "testcraft.devMode";

interface DevModeContextValue {
  devMode: boolean;
  setDevMode: (on: boolean) => void;
}

const DevModeContext = createContext<DevModeContextValue>({
  devMode: false,
  setDevMode: () => {},
});

export function DevModeProvider({ children }: { children: ReactNode }) {
  const [devMode, setDevModeState] = useState<boolean>(() => {
    try {
      return typeof window !== "undefined" && localStorage.getItem(DEV_MODE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const setDevMode = (on: boolean) => {
    setDevModeState(on);
    try {
      localStorage.setItem(DEV_MODE_KEY, on ? "1" : "0");
    } catch {
      /* private mode — keep in-memory only */
    }
  };

  return (
    <DevModeContext.Provider value={{ devMode, setDevMode }}>
      {children}
    </DevModeContext.Provider>
  );
}

export function useDevMode() {
  return useContext(DevModeContext);
}
